import { Container, Graphics, Text, TextStyle, type FederatedPointerEvent } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { getActiveDevice } from '@/input/active-device';
import { t } from '@/i18n';
import type { InputState } from '@/input/types';
import { sfx } from '@/audio/sfx';
import { houseGlyph } from './home-art';
import { chamferPanel, label, targetBrackets } from './widgets';
import { SETTINGS_ROWS } from './settings-rows';

/** Layout: preferred row pitch/height, squeezed to fit short screens */
const ROW_W = 660;
const ROW_PITCH = 74;
const ROW_H = 56;
const ROW_GAP = 10;
const TOP_FRAC = 0.24;
/** Space reserved under the rows for BACK + the device hint */
const FOOTER_H = 120;
const BTN_W = 260;
const BTN_H = 52;
/** Bottom row with MAIN MENU: the two buttons sit this far off centre */
const BTN_SPREAD = 145;
/**
 * Phones (polish 1): when one column would squeeze a row under the touch
 * floor, the rows go into TWO columns with label over value, and the title
 * and footer shrink.
 */
const MIN_PITCH = 44;
const SHORT = { top: 72, footer: 92, title: 28, colGap: 16, colMaxW: 330, margin: 16, gap: 4 } as const;

/** Row grid for this canvas: 1 column (TV / desktop) or 2 (short phones). */
function rowGrid(screenW: number, screenH: number, n: number) {
  const top = screenH * TOP_FRAC;
  const pitch = (screenH - top - FOOTER_H) / n;
  if (pitch >= MIN_PITCH) {
    const p = Math.min(ROW_PITCH, pitch);
    const rowH = Math.min(ROW_H, p - ROW_GAP);
    return { cols: 1, perCol: n, top, pitch: p, rowH, colW: ROW_W, titleSize: 44, titleY: top / 2 };
  }
  const perCol = Math.ceil(n / 2);
  const p = Math.min(ROW_PITCH, (screenH - SHORT.top - SHORT.footer) / perCol);
  const colW = Math.min(SHORT.colMaxW, (screenW - SHORT.margin * 2 - SHORT.colGap) / 2);
  const rowH = Math.min(ROW_H, p - SHORT.gap);
  // Title centred in the strip above the first row
  const titleY = (SHORT.top - rowH / 2) / 2;
  return { cols: 2, perCol, top: SHORT.top, pitch: p, rowH, colW, titleSize: SHORT.title, titleY };
}

/** Hover-focus is for the mouse: on touch the rebuild ate the tap (wave 4 rule). */
const mouseOnly = (e: FederatedPointerEvent): boolean => e.pointerType === 'mouse';

/**
 * Settings screen (pause). Every row and the BACK button are clickable;
 * keyboard/gamepad: ↑/↓ select, ←/→ previous/next value, Space/A next,
 * Esc/B/Start back. Opened as the in-run PAUSE it also offers ⌂ MAIN MENU
 * (polish 1) — ←/→ switch between it and BACK. Hints adapt to the device.
 */
export class SettingsScreen {
  readonly view = new Container();
  private readonly dim = new Graphics();
  private readonly layer = new Container();
  open = false;
  private selected = 0;
  private prevMoveY = 0;
  private prevMoveX = 0;
  private screenW = 0;
  private screenH = 0;
  /** In-run pause: the ⌂ MAIN MENU action (null = opened from the title) */
  private onHome: (() => void) | null = null;

  private readonly rows = SETTINGS_ROWS;

  /** Focus list = rows, [⌂ MAIN MENU], BACK (last index) */
  private get homeIdx(): number {
    return this.onHome ? this.rows.length : -1;
  }

  private get backIdx(): number {
    return this.rows.length + (this.onHome ? 1 : 0);
  }

  constructor() {
    this.view.visible = false;
    this.view.addChild(this.dim, this.layer);
  }

  /** @param onHome in-run pause: shows ⌂ MAIN MENU (run/home-flow.ts) */
  show(screenW: number, screenH: number, onHome: (() => void) | null = null): void {
    this.open = true;
    this.view.visible = true;
    this.onHome = onHome;
    this.selected = 0;
    this.resize(screenW, screenH);
  }

  /** Refit to a new canvas size, keeping focus and the home action. */
  resize(screenW: number, screenH: number): void {
    this.screenW = screenW;
    this.screenH = screenH;
    this.rebuild();
  }

  private rebuild(): void {
    const { screenW, screenH } = this;
    this.dim.clear().rect(0, 0, screenW, screenH).fill({ color: 0x0a0614, alpha: 0.85 });
    this.layer.removeChildren().forEach((c) => c.destroy({ children: true }));

    const grid = rowGrid(screenW, screenH, this.rows.length);
    const { top, pitch, rowH, colW } = grid;
    const title = new Text({
      text: t('settings.title'),
      style: new TextStyle({
        fontFamily: 'Tektur, Arial Black, sans-serif', fontSize: grid.titleSize,
        fill: COLORS.uiAccent, letterSpacing: 6,
      }),
    });
    title.anchor.set(0.5);
    title.position.set(screenW / 2, grid.titleY);
    this.layer.addChild(title);

    const gridW = grid.cols * colW + (grid.cols - 1) * SHORT.colGap;
    this.rows.forEach((row, i) => {
      const col = Math.floor(i / grid.perCol);
      const x = screenW / 2 - gridW / 2 + col * (colW + SHORT.colGap);
      const y = top + (i % grid.perCol) * pitch;
      const selected = i === this.selected;
      const panel = new Container();

      const bg = new Graphics();
      chamferPanel(bg, colW, rowH, {
        fill: selected ? 0x2a2440 : 0x161222,
        stroke: selected ? COLORS.uiAccent : 0x2a2440,
        strokeWidth: selected ? 4 : 2,
        notch: 12,
      });
      bg.position.set(x, y - rowH / 2);
      if (selected) targetBrackets(bg, colW, rowH, COLORS.uiAccent, 14, 4);

      const stacked = grid.cols > 1;
      const label = new Text({
        text: row.label(),
        style: new TextStyle({ fontFamily: 'Arial', fontSize: stacked ? 14 : 22, fill: 0xffffff }),
      });
      const value = new Text({
        text: `< ${row.value()} >`,
        style: new TextStyle({
          fontFamily: 'Arial', fontSize: stacked ? 15 : 20,
          fill: selected ? COLORS.playerYellow : 0x9aa0a6,
        }),
      });
      if (stacked) {
        // Phone: label over value, both left-aligned in the cell
        label.anchor.set(0, 0.5);
        label.position.set(x + 14, y - rowH / 4);
        value.anchor.set(0, 0.5);
        value.position.set(x + 14, y + rowH / 4);
        const room = colW - 28;
        if (value.width > room) value.scale.set(room / value.width);
        if (label.width > room) label.scale.set(room / label.width);
      } else {
        label.anchor.set(0, 0.5);
        label.position.set(screenW / 2 - 300, y);
        value.anchor.set(1, 0.5);
        value.position.set(screenW / 2 + 300, y);
      }

      panel.addChild(bg, label, value);
      panel.eventMode = 'static';
      panel.cursor = 'pointer';
      panel.on('pointerover', (e: FederatedPointerEvent) => {
        if (mouseOnly(e)) this.select(i);
      });
      panel.on('pointertap', () => this.activate(i, 1));
      this.layer.addChild(panel);
    });

    // BACK (+ ⌂ MAIN MENU in-run) — explicit, clickable (console + mouse UX)
    const btnY = top + grid.perCol * pitch + 4;
    const spread = this.onHome ? BTN_SPREAD : 0;
    if (this.onHome) this.layer.addChild(this.button(this.homeIdx, screenW / 2 - spread, btnY));
    this.layer.addChild(this.button(this.backIdx, screenW / 2 + spread, btnY));

    const hint = new Text({
      text: t(getActiveDevice() === 'gamepad' ? 'settings.hint.gamepad' : getActiveDevice() === 'touch' ? 'settings.hint.touch' : 'settings.hint.keyboard'),
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 16, fill: 0x6a7076 }),
    });
    hint.anchor.set(0.5);
    hint.position.set(screenW / 2, screenH - 36);
    this.layer.addChild(hint);
  }

  /** BACK / ⌂ MAIN MENU button centred on (x, y). */
  private button(idx: number, x: number, y: number): Container {
    const selected = this.selected === idx;
    const home = idx === this.homeIdx;
    const btn = new Container();
    const bg = new Graphics();
    chamferPanel(bg, BTN_W, BTN_H, {
      fill: selected ? 0x2a2440 : 0x161222,
      stroke: selected ? COLORS.uiAccent : 0x4a4256,
      strokeWidth: selected ? 4 : 3,
      notch: 12,
    });
    bg.position.set(-BTN_W / 2, -BTN_H / 2);
    if (selected) targetBrackets(bg, BTN_W, BTN_H, COLORS.uiAccent, 12, 4);
    const txt = label(t(home ? 'pause.home' : 'settings.back'), 19, 0xffffff, 2);
    txt.anchor.set(0.5);
    btn.addChild(bg, txt);
    if (home) {
      // House first: the word is secondary (4-year-old rule)
      const icon = houseGlyph(new Graphics(), 0, 0, 13, selected ? COLORS.uiAccent : 0xd5d9de);
      const w = 34 + txt.width;
      icon.position.set(-w / 2 + 13, 0);
      txt.position.set(-w / 2 + 34 + txt.width / 2, 1);
      btn.addChild(icon);
    }
    btn.position.set(x, y);
    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    btn.on('pointerover', (e: FederatedPointerEvent) => {
      if (mouseOnly(e)) this.select(idx);
    });
    btn.on('pointertap', () => this.activate(idx, 1));
    return btn;
  }

  private select(i: number): void {
    if (i !== this.selected) {
      this.selected = i;
      this.rebuild();
    }
  }

  private activate(i: number, dir: 1 | -1): void {
    if (i === this.homeIdx) {
      sfx.uiConfirm();
      this.onHome?.(); // the confirm floats above; ✕ comes back here
      return;
    }
    if (i >= this.rows.length) {
      this.hide();
      return;
    }
    this.rows[i]!.change(dir);
    sfx.uiConfirm();
    this.rebuild();
  }

  hide(): void {
    this.open = false;
    this.view.visible = false;
    this.onHome = null;
  }

  /** ↑/↓: rows in order; the bottom button row counts as ONE row (wraps). */
  private verticalStep(dir: number): number {
    const n = this.rows.length;
    const bottom = this.onHome ? this.homeIdx : this.backIdx;
    if (this.selected >= n) return dir > 0 ? 0 : n - 1;
    const next = this.selected + dir;
    return next < 0 || next >= n ? bottom : next;
  }

  /** Consumes input while open. @returns true if the game update must be skipped */
  handleInput(input: InputState): boolean {
    if (!this.open) return false;

    const moveYEdge =
      Math.abs(input.moveY) > 0.5 && Math.abs(this.prevMoveY) <= 0.5
        ? Math.sign(input.moveY)
        : 0;
    this.prevMoveY = input.moveY;
    const moveXEdge =
      Math.abs(input.moveX) > 0.5 && Math.abs(this.prevMoveX) <= 0.5
        ? Math.sign(input.moveX)
        : 0;
    this.prevMoveX = input.moveX;

    if (moveYEdge !== 0) {
      this.selected = this.verticalStep(moveYEdge);
      sfx.uiMove();
      this.rebuild();
    }
    // ←/→ step a row's value; on the bottom row they hop ⌂ MAIN MENU ↔ BACK
    if (moveXEdge !== 0 && this.selected < this.rows.length) {
      this.activate(this.selected, moveXEdge > 0 ? 1 : -1);
    } else if (moveXEdge !== 0 && this.onHome) {
      this.selected = this.selected === this.homeIdx ? this.backIdx : this.homeIdx;
      sfx.uiMove();
      this.rebuild();
    }
    if (input.dashPressed) this.activate(this.selected, 1);
    // Esc / B / Start all go back
    if (input.pausePressed || input.altPressed) this.hide();
    return true;
  }
}
